// Lib
import mongoose from 'mongoose';
import { RawDraftContentState } from 'draft-js';

// Types
import { Point } from '../maths/geometry/pointTypes';
import { MNTableContent } from '../table/TableTypes';
import { TiptapContent } from '../tiptap/tiptapTypes';

// Constants
import { BoardSections } from '../boards/boardConstants';
import { ImMap } from '../utils/immutableHelper';
import { ElementType } from './elementTypes';
import { ElementEditor } from './elementEditorConstants';
import { ColorObject } from '../colors/colorObjectUtil';
import { MediaOnlyDisplay } from '../../client/element/resizing/resizeTypes';

export enum AttachMode {
    // For backwards compatibility, the default position mode (pixel / relative coordinates) is represented
    //  by `attachMode === undefined`
    inline = 'inline',
}

export interface MNElementPosition {
    addedDate?: number;
    order?: number;
    score?: number;
    index?: number;
    restoreLocation?: MNElementLocation;
    attachMode?: AttachMode;
    x?: number | string;
    y?: number | string;
}

export type MNElementLocation = Partial<{
    parentId: string;
    // When using enums in schema, have to accept null as a value to pass validation
    section: BoardSections | null;
    position: MNElementPosition;
    rootBoard: boolean;
}>;

export interface MNElementMeta {
    creator: mongoose.Types.ObjectId | string;
    modifiedBy: mongoose.Types.ObjectId | string;
    createdTime: number;
    modifiedTime: number;
    // FIXME Platforms enum
    platform: string;
    significantModified: number;
    locationSectionModifiedTime: number;
    aclModifiedTime?: number;
    versionId: string;
    // Set on the client side but not persisted
    modifiedVersionId?: string;

    // Temporary fields used by the permanentlyRemoveDeletedElements.ts function
    safeToDelete?: boolean;
    safeToDeleteTimestamp?: number;
}

export interface MNImageContentMeta {
    primaryColor: string;
    width: number;
    height: number;
    colors: string[];
    transparent: boolean;
    thumb: string;
    isDataUri: boolean;
}

export interface MNImageContentUrls {
    original: string;
    large: string;
    regular: string;
    huge: string;
}

export interface MNImageContentError {
    error: {
        message: string;
    };
    hasError: boolean;
}

export type MNImageContent = Partial<MNImageContentMeta> & Partial<MNImageContentUrls> & Partial<MNImageContentError>;

export enum MNImageBackgroundRemovalVariantOptions {
    withBackground = 'withBackground',
    withoutBackground = 'withoutBackground',
}

export type MNImageBackgroundRemovalVariantImages = Partial<
    Record<MNImageBackgroundRemovalVariantOptions, MNImageContent>
>;

export type MNImageBackgroundRemovalContent = Partial<{
    active: MNImageBackgroundRemovalVariantOptions;
    sources: MNImageBackgroundRemovalVariantImages;
    rendered: MNImageBackgroundRemovalVariantImages | null;
}>;

export type MNFileContent = Partial<{
    type: string;
    ext: string;
    mime: string;
    filename: string;
    size: number;
    lastModified: number;
    url: string;
    uploadedTimestamp: number;
    modifiedTime: number;
}>;

export type MNMediaContent = Partial<{
    width: number;
    height: number;
    originalWidth: string;
    originalHeight: string;
    srcType: string;
    html: string;
}>;

export type MNImageEditorDataContent = Partial<{
    x: number;
    y: number;
    width: number;
    height: number;
    rotate: number;
    scaleX: number;
    scaleY: number;
}>;

export type MNDrawingPoint = [number, number] | [number, number, number];

export type MNDrawingPath = Partial<{
    id: string;
    points: MNDrawingPoint[];
    size: number;
    color: string;
}>;

export type MNDrawingContent = Partial<{
    width: number;
    height: number;
    svg: string;
    paths: MNDrawingPath[];
}>;

export type MNLinePosition = Partial<{
    x: number;
    y: number;
    snapped: boolean;
    elementId: string;
}>;

export type MNElementContentLink = Partial<{
    url: string;
    type: string;
    title: string;
    favicon: string;
}>;

export type MNElementContentLinkProvider = Partial<{
    url: string;
    name: string;
    display: string;
}>;

export type MNReactionContent = Partial<{
    userId: string;
    emojiCode: string;
    timestamp: number;
}>;

export type EditorContent = RawDraftContentState | TiptapContent | null;
export type ImEditorContent = ImMap<RawDraftContentState> | ImMap<TiptapContent> | null;

export type MNElementContent = Partial<{
    width: number | null;
    media: MNMediaContent | null;
    attachmentId: string;

    editor?: ElementEditor;
    textContent: EditorContent;
    caption: EditorContent;
    captionModified: boolean;

    background: {
        color: string | null;
    } | null;
    transparent: {
        enabled: boolean;
    };
    icon: {
        type: string;
        id: string;
        name: string;
        svg: string;
    };
    color:
        | /* most element types: */ string
        | /* color swatches */ {
              space: string;
              value: string;
          }
        | ColorObject
        | null;
    secondaryColor: string | null;
    autoColorIndex: number | null;
    title: string;
    titleSuggestionAck: boolean;
    showTitle: boolean;
    showCaption: boolean;
    mediaOnlyDisplay: MediaOnlyDisplay;
    showColorValue: boolean;
    image: MNImageContent;
    imageOriginal: MNImageContent;
    backgroundRemoval: MNImageBackgroundRemovalContent;
    file: MNFileContent;
    imageEditorData: MNImageEditorDataContent;
    rootType: string;
    inbox: string;
    keywords: string[];
    mediaType: string;
    link: MNElementContentLink;
    provider: MNElementContentLinkProvider;
    authors: string[];
    previewReady: boolean;
    tableContent: MNTableContent;
    drawing: MNDrawingContent;

    reactions: MNReactionContent[];
    labels: string[];

    // Board specific props
    publicEditEnabled: boolean | null;
    publicEditEnabledTimestamp: number | null;
    publishedPasswordProtected: boolean | null;
    publishedTimestamp: number | null;
    publishedFeedbackEnabled: boolean | null;
    published: boolean | null;
    canvasOrigin: Point;
    previousCanvasOrigin: Point | null;

    // Template board specific props
    templateLink: string | null; // Link to the "empty" state template board if this is a public template board

    // Comment specific props
    threadId: string;

    // Line Specific props
    start: MNLinePosition;
    end: MNLinePosition;
    startStyle: string;
    endStyle: string;
    lineStyle: string;
    lineWeight: number;

    // Alias specific props
    originalColor: string;
    originalTitle: string;
    originalIcon: Partial<{ type: string; id: string; string: string }>;
    linkTo: string;

    // Resizable editor height props
    minHeight: number | null;
    maxHeight: number | null;
    autoHeight: boolean;
}>;

// FIXME Ensure all properties are listed
export interface MNElement {
    _id: string;
    // On the client side the element will have an id
    id?: string;
    elementType: ElementType;
    location?: MNElementLocation;
    content: MNElementContent;
    acl?: { [access: string]: number };
    meta?: MNElementMeta;
}

export type ImMNElement = ImMap<MNElement>;

export type MNElementMap = {
    [id: string]: MNElement;
};
export type ImMNElementMap = ImMap<{ [id: string]: ImMNElement }>;

/**
 * Currently only used on the client side.
 */
export type MNElementSummary = {
    _id: string;
    elementType: ElementType;
    location: {
        section: BoardSections | null;
        parentId: string | null;
    };
    content?: {
        linkTo?: string;
    };
};
